import { getCalculatorProductsPrice } from '~/services/calculator.service'
import type {
    ICalculator,
    ICalculatorCity,
    ICalculatorOption,
    ICalculatorRawSetting,
    ICalculatorSelectedValues,
    ICalculatorSetting,
    ICalculatorSettingField,
    ICalculatorSettingUpdaters,
} from '~/types/calculator.types'

// todo: прогнать через типограф
const ARENDA_RADIOGUIDE_DEFAULT_CITY_MESSAGES = {
    spb: 'Стоимость указана за&nbsp;самовывоз из&nbsp;нашего офиса в&nbsp;Санкт-Петербурге. Если вам необходима доставка, напишите в&nbsp;комментарии адрес, дату и&nbsp;время',
    moscow: 'Стоимость указана за&nbsp;самовывоз из&nbsp;нашего офиса в&nbsp;Москве. Если вам необходима доставка, напишите в&nbsp;комментарии адрес, дату и&nbsp;время',
    other: 'Стоимость указана за&nbsp;фактические дни работы в&nbsp;вашем городе, для расчета стоимости доставки и&nbsp;дней простоя оборудования напишите в&nbsp;комментарии адрес и&nbsp;даты мероприятия',
}

const ARENDA_RADIOGUIDE_DEFAULT_CITIES: ICalculatorCity[] = [
    {
        value: 1,
        label: 'Москва',
    },
    {
        value: 2,
        label: 'Санкт-Петербург',
    },
    {
        value: 'other',
        label: 'Другой',
    },
]

export const arendaRadioguideCalculatorRawSetting: ICalculatorRawSetting = {
    fields: [
        {
            title: 'Приемник с наушником',
            slug: 'q1',
            min_count: 1,
            max_count: 2000,
            default_disabled: false,
            default_hidden: false,
        },
        {
            title: 'Передатчик с головным микрофоном',
            slug: 'q2',
            min_count: 1,
            max_count: 12,
            default_disabled: false,
            default_hidden: false,
        },
        {
            title: 'Количество дней',
            slug: 'q3',
            min_count: 1,
            max_count: 360,
            default_disabled: false,
            default_hidden: false,
        },
        {
            title: 'Город',
            slug: 'city',
            options: [],
            default_disabled: false,
            default_hidden: false,
        },
    ],
    default_values: {
        q1: 47,
        q2: 1,
        q3: 1,
        city: '2',
    },
    fields_modal: [
        {
            type: 'counter',
            slug: 'q1',
            prevent_hidden: false,
        },
        {
            type: 'counter',
            slug: 'q2',
            prevent_hidden: false,
        },
        {
            type: 'counter',
            slug: 'q3',
            prevent_hidden: false,
        },
        {
            type: 'select',
            slug: 'city',
            prevent_hidden: false,
        },
    ],
    fields_order: ['q1', 'q2', 'q3', 'city'],
    images: [
        {
            counter_from: 'q1',
            success_icon: false,
            hide_count: false,
            align: 'row',
            size: 'small',
            images: [
                {
                    image: '/assets/images/products/radioguide/jpg-png/reinvox-r-300.png',
                    image_webp:
                        '/assets/images/products/radioguide/webp/reinvox-r-300.webp',
                    values: {},
                },
            ],
        },
        {
            counter_from: 'q2',
            success_icon: false,
            hide_count: false,
            align: 'row',
            size: 'small',
            images: [
                {
                    image: '/assets/images/products/radioguide/jpg-png/reinvox-t-300.png',
                    image_webp:
                        '/assets/images/products/radioguide/webp/reinvox-t-300.webp',
                    values: {},
                },
            ],
        },
        {
            counter_from: '',
            success_icon: false,
            hide_count: true,
            align: 'row',
            size: 'big',
            images: [
                {
                    image: '/assets/images/products/radioguide/jpg-png/cases.png',
                    image_webp:
                        '/assets/images/products/radioguide/webp/cases.webp',
                    values: {},
                },
            ],
        },
    ],
    price_modificators: {},
    prices: {},
    meta: {},
}

export const arendaRadioguideUpdaters: ICalculatorSettingUpdaters = {}

export function getArendaRadioguideCalculatorSetting(
    calculator: ICalculator | null
): ICalculatorSetting | null {
    if (!calculator) return null

    const setting: ICalculatorSetting = JSON.parse(
        JSON.stringify(arendaRadioguideCalculatorRawSetting)
    )

    const cityField = setting.fields.find(
        (field: ICalculatorSettingField) => field.slug === 'city'
    )

    const cities = normalizeArendaRadioguideCities(calculator.cities)

    if (cityField) {
        cityField.options = cities
    }

    const defaultCity = getDefaultArendaRadioguideCity(cities)

    setting.default_values.city = defaultCity

    if (Array.isArray(calculator.prices)) {
        calculator.prices.forEach((price) => {
            setting.prices[price.calculator_variable_name] = price.price
        })
    }

    setting.meta = {
        city_messages: {
            ...ARENDA_RADIOGUIDE_DEFAULT_CITY_MESSAGES,
            ...calculator.city_messages,
        },
        minimums: {
            spb: calculator.minimums?.spb || 'MinSpb',
            moscow: calculator.minimums?.moscow || 'MinMos',
        },
    }

    setting.updaters = arendaRadioguideUpdaters
    setting.getPrice = arendaRadioguideGetPrice
    setting.getTotalDescription = arendaRadioguideGetTotalDescription

    return setting
}

export function arendaRadioguideGetPrice(
    calculator: ICalculatorSetting,
    selectedValues: ICalculatorSelectedValues
): string {
    const productPrices = getCalculatorProductsPrice(calculator, selectedValues)
    const priPrice = getArendaRadioguidePriceByAliases(productPrices, [
        'Pri',
        'pri',
    ])
    const perPrice = getArendaRadioguidePriceByAliases(productPrices, [
        'Per',
        'per',
    ])

    const total =
        (Number(selectedValues.q1) * priPrice +
            Number(selectedValues.q2) * perPrice) *
        Number(selectedValues.q3)

    return formatPrice(
        Math.max(total, getArendaRadioguideMinimum(calculator, selectedValues))
    )
}

export function arendaRadioguideGetTotalDescription(
    calculator: ICalculatorSetting,
    selectedValues: ICalculatorSelectedValues
): string {
    const cityMessageKey = getArendaRadioguideCityMessageKey(
        selectedValues.city
    )

    return ARENDA_RADIOGUIDE_DEFAULT_CITY_MESSAGES[cityMessageKey]

    // return (
    //     cityMessages?.[cityMessageKey] ||
    //     ARENDA_RADIOGUIDE_DEFAULT_CITY_MESSAGES.other
    // )
}

function getArendaRadioguidePriceByAliases(
    prices: { [key: string]: number },
    aliases: string[]
): number {
    for (const alias of aliases) {
        if (alias in prices) {
            return Number(prices[alias] || 0)
        }
    }

    return 0
}

function getArendaRadioguideMinimum(
    calculator: ICalculatorSetting,
    selectedValues: ICalculatorSelectedValues
): number {
    const cityKey = getArendaRadioguideCityMessageKey(selectedValues.city)

    if (cityKey === 'other') {
        return 0
    }

    const minimums = calculator.meta?.minimums as
        | {
              spb?: string
              moscow?: string
          }
        | undefined
    const minimumPriceSlug =
        cityKey === 'spb'
            ? minimums?.spb || 'MinSpb'
            : minimums?.moscow || 'MinMos'

    return Number(calculator.prices[minimumPriceSlug] || 0)
}

function getArendaRadioguideCityMessageKey(city: string | number | undefined) {
    const normalizedCity = String(city)

    if (normalizedCity === '2') {
        return 'spb'
    }

    if (normalizedCity === '1') {
        return 'moscow'
    }

    return 'other'
}

function getDefaultArendaRadioguideCity(options: ICalculatorOption[]) {
    const spbCity = options.find((option) => option.name === 'Санкт-Петербург')

    if (spbCity) {
        return spbCity.calculator_variable_name
    }

    return options[0]?.calculator_variable_name || '2'
}

function normalizeArendaRadioguideCities(
    cities: ICalculator['cities']
): ICalculatorOption[] {
    const preparedCities =
        Array.isArray(cities) && cities.length
            ? cities
            : ARENDA_RADIOGUIDE_DEFAULT_CITIES

    return preparedCities.map((city) => ({
        id: city.value,
        name: city.label,
        calculator_variable_name: String(city.value),
        hint: null,
        image: null,
        product: null,
    }))
}
